import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import PersonCell from '../../../components/ui/PersonCell';

const STATUS_OPTIONS = ['candidate', 'hire', 'employee', 'leaver', 'alumni'];

function csvMultiFilter(label, options, optionLabel = (o) => o) {
  return {
    defaultOp: 'in',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0]
        ? filterList[index][0].split(',').filter(Boolean)
        : [];
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              const csv = Array.isArray(v) ? v.filter(Boolean).join(',') : '';
              filterList[index] = csv ? [csv] : [];
              onChange(filterList[index], index, column);
            }}
            renderValue={(sel) => sel.map((s) => optionLabel(s)).join(', ')}
          >
            {options.map((o) => (
              <MenuItem key={o} value={o}>{optionLabel(o)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
  };
}

const useLifecyclePersonsApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/lifecycle-persons?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const setStatus = useCallback(async (id, status) => {
    try {
      await api.post(`/v1/lifecycle-persons/${id}/status`, { status });
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const statusLabel = useCallback((s) => t(`lifecyclePerson.status.${s}`, s), [t]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'fullName',
      label: t('lifecyclePerson.fullName'),
      options: {
        customBodyRender: (v, tableMeta) => (
          <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.profilePicture} />
        ),
        filter: true, sort: false,
        filterType: 'textField',
        filterOptions: { defaultOp: 'like' },
      },
    },
    {
      name: 'email',
      label: t('lifecyclePerson.email'),
      options: {
        filter: true, sort: false,
        filterType: 'textField',
        filterOptions: { defaultOp: 'like' },
      },
    },
    {
      name: 'phone',
      label: t('lifecyclePerson.phone'),
      options: {
        filter: true, sort: false,
        filterType: 'textField',
        filterOptions: { defaultOp: 'like' },
        customBodyRender: (v) => v || '-',
      },
    },
    {
      name: 'status',
      label: t('lifecyclePerson.status.label'),
      options: {
        filter: true, sort: false,
        filterType: 'custom',
        customBodyRender: (v) => statusLabel(v),
        filterOptions: csvMultiFilter(t('lifecyclePerson.status.label'), STATUS_OPTIONS, statusLabel),
      },
    },
    {
      name: 'currentEmployeeId',
      label: t('lifecyclePerson.currentEmployeeId'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => v || '-',
      },
    },
    {
      name: 'currentCandidateId',
      label: t('lifecyclePerson.currentCandidateId'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => v || '-',
      },
    },
    {
      name: 'originalEmployeeId',
      label: t('lifecyclePerson.originalEmployeeId'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => v || '-',
      },
    },
    dateFilterColumn('createDate', t('lifecyclePerson.createDate'), lang, { includeTime: true }),
  ], [t, lang, statusLabel, data]);

  return { fetching, data, pagination, columns, refresh, setStatus, statusLabel };
};

export default useLifecyclePersonsApi;
